/**
 * Wraps `fn` so rapid calls collapse into a single call `delayMs` after the
 * last one — e.g. typing into a search box firing one request per pause in
 * typing instead of one per keystroke. Same idea as vueuse's `useDebounceFn`,
 * reimplemented locally: this is a Svelte app, and pulling in `@vueuse/core`
 * would pull in Vue itself (a required peer dependency) just for this one
 * function.
 */
export function useDebouncedFn<Args extends unknown[]>(
  fn: (...args: Args) => void,
  delayMs: number,
): ((...args: Args) => void) & { cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | undefined

  function cancel(): void {
    if (timer !== undefined) {
      clearTimeout(timer)
      timer = undefined
    }
  }

  function debounced(...args: Args): void {
    cancel()
    timer = setTimeout(() => fn(...args), delayMs)
  }

  debounced.cancel = cancel
  return debounced
}
